/**
 * Sampler slice / explode helpers (S2).
 * Slices live on SamplerPreset and reference frames of primarySampleId.
 */
import type { SampleSlice, SamplerPreset, SamplerZone } from "../../../../src/lib/audio";
import {
  SAMPLER_PAD_BASE_NOTE,
  SAMPLER_PAD_COUNT,
} from "../../../../src/lib/audio";

export { SAMPLER_PAD_COUNT, SAMPLER_PAD_BASE_NOTE };

function newId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function sortedSlices(slices: SampleSlice[] | undefined): SampleSlice[] {
  return (slices ?? []).slice().sort((a, b) => a.frame - b.frame || a.id.localeCompare(b.id));
}

export function addSliceAt(
  slices: SampleSlice[] | undefined,
  frame: number,
  label?: string,
): SampleSlice[] {
  const next = sortedSlices(slices);
  const rounded = Math.max(0, Math.round(frame));
  // Avoid exact duplicates within 1 frame.
  if (next.some(s => Math.abs(s.frame - rounded) <= 1)) {
    return next;
  }
  next.push({
    id: newId("slice"),
    frame: rounded,
    ...(label ? { label } : {}),
  });
  return sortedSlices(next);
}

export function removeSlice(slices: SampleSlice[] | undefined, id: string): SampleSlice[] {
  return sortedSlices(slices).filter(s => s.id !== id);
}

export function nudgeSlice(
  slices: SampleSlice[] | undefined,
  id: string,
  frame: number,
  maxFrame: number,
): SampleSlice[] {
  return sortedSlices(slices).map(s =>
    s.id === id
      ? { ...s, frame: Math.max(0, Math.min(maxFrame, Math.round(frame))) }
      : s,
  );
}

/** Toggle whether the interval starting at this slice is skipped by explode. */
export function toggleSliceOmit(
  slices: SampleSlice[] | undefined,
  id: string,
): SampleSlice[] {
  return sortedSlices(slices).map(s => {
    if (s.id !== id) {
      return s;
    }
    if (s.omit) {
      const { omit: _drop, ...rest } = s;
      return rest;
    }
    return { ...s, omit: true };
  });
}

export type ExplodeOptions = {
  sampleId: string;
  sampleLength: number;
  baseNote?: number;
  /** How many pads/zones to fill (default SAMPLER_PAD_COUNT). */
  padCount?: number;
};

/**
 * Explode sorted slices into contiguous zones / pad bindings.
 * Interval i plays [slice_i, slice_{i+1}) — head (before first) and tail
 * (after last) are not mapped to pads. Markers with `omit` skip their
 * starting interval. Needs ≥2 slices.
 */
export function explodeSlicesToZones(
  slices: SampleSlice[] | undefined,
  options: ExplodeOptions,
): SamplerZone[] {
  const sampleLen = Math.max(1, options.sampleLength);
  const sorted = sortedSlices(slices)
    .map(s => ({ ...s, frame: Math.max(0, Math.min(sampleLen, s.frame)) }))
    .filter((s, i, arr) => i === 0 || s.frame !== arr[i - 1]!.frame);
  const base = options.baseNote ?? SAMPLER_PAD_BASE_NOTE;
  const sampleId = options.sampleId;

  const zones: SamplerZone[] = [];
  const maxPads = options.padCount ?? SAMPLER_PAD_COUNT;
  for (let i = 0; i < sorted.length - 1 && zones.length < maxPads; i++) {
    if (sorted[i]!.omit) {
      continue;
    }
    const start = sorted[i]!.frame;
    const end = sorted[i + 1]!.frame;
    if (end <= start) {
      continue;
    }
    const note = Math.min(127, base + zones.length);
    zones.push({
      id: newId("zone"),
      sampleId,
      keyLow: note,
      keyHigh: note,
      rootKey: note,
      velLow: 0,
      velHigh: 127,
      region: { start, end },
      padIndex: zones.length,
    });
  }

  if (zones.length === 0) {
    zones.push({
      id: newId("zone"),
      sampleId,
      keyLow: 0,
      keyHigh: 127,
      rootKey: 60,
      velLow: 0,
      velHigh: 127,
      region: { start: 0, end: sampleLen },
    });
  }

  return zones;
}

/** Resolve the zone mapped to a pad (padIndex first, then exclusive keyLow). */
export function resolvePadZone(
  zones: readonly SamplerZone[],
  padIndex: number,
  baseNote = SAMPLER_PAD_BASE_NOTE,
): SamplerZone | undefined {
  const byPad = zones.find(z => z.padIndex === padIndex);
  if (byPad) {
    return byPad;
  }
  const note = baseNote + padIndex;
  return zones.find(
    z => z.padIndex == null && z.keyLow === note && z.keyHigh === note,
  );
}

/**
 * Swap (or move) chop assignments between two pads.
 * Empty pads are allowed — filled↔empty moves the chop; empty↔empty is a no-op.
 */
export function swapPadAssignments(
  zones: SamplerZone[],
  padA: number,
  padB: number,
  options?: { baseNote?: number; padCount?: number },
): SamplerZone[] {
  const padCount = options?.padCount ?? SAMPLER_PAD_COUNT;
  const base = options?.baseNote ?? SAMPLER_PAD_BASE_NOTE;
  if (
    padA === padB
    || padA < 0
    || padB < 0
    || padA >= padCount
    || padB >= padCount
  ) {
    return zones;
  }
  const zoneA = resolvePadZone(zones, padA, base);
  const zoneB = resolvePadZone(zones, padB, base);
  if (!zoneA && !zoneB) {
    return zones;
  }
  if (zoneA && zoneB && zoneA.id === zoneB.id) {
    return zones;
  }
  const noteA = Math.min(127, base + padA);
  const noteB = Math.min(127, base + padB);
  return zones.map(z => {
    if (zoneA && z.id === zoneA.id) {
      return { ...z, padIndex: padB, keyLow: noteB, keyHigh: noteB, rootKey: noteB };
    }
    if (zoneB && z.id === zoneB.id) {
      return { ...z, padIndex: padA, keyLow: noteA, keyHigh: noteA, rootKey: noteA };
    }
    return z;
  });
}

/** One full-keyboard zone covering the sample (undo explode). */
export function singleZoneFromSample(
  sampleId: string,
  sampleLength: number,
  rootKey = 60,
): SamplerZone[] {
  return [
    {
      id: newId("zone"),
      sampleId,
      keyLow: 0,
      keyHigh: 127,
      rootKey: Math.max(0, Math.min(127, Math.round(rootKey))),
      velLow: 0,
      velHigh: 127,
      region: { start: 0, end: Math.max(1, sampleLength) },
    },
  ];
}

export function applyExplodeToPreset(
  preset: SamplerPreset,
  sampleLength: number,
): SamplerPreset {
  const sampleId = preset.primarySampleId ?? preset.zones[0]?.sampleId;
  if (!sampleId) {
    return preset;
  }
  const zones = explodeSlicesToZones(preset.slices, {
    sampleId,
    sampleLength,
  });
  return {
    ...preset,
    zones,
    // After explode, classic one-shots usually don't loop the whole asset.
    loop: { ...preset.loop, enabled: false },
  };
}

/** One playable sampler pad region ready to export as its own sample. */
export type SamplerPadRegion = {
  padIndex: number;
  sampleId: string;
  /** Inclusive start frame. */
  start: number;
  /**
   * Exclusive end frame. `0` means “through end of sample” — resolve against
   * PCM length at export time.
   */
  end: number;
  name: string;
};

/**
 * List filled sampler pads (padIndex map or exclusive C4+ keys) with regions.
 * Empty pads are skipped. Prefer `zone.region`; otherwise start=0, end=0 (full).
 */
export function listSamplerPadRegions(
  preset: SamplerPreset,
  options?: { padCount?: number; baseNote?: number },
): SamplerPadRegion[] {
  const padCount = options?.padCount ?? SAMPLER_PAD_COUNT;
  const base = options?.baseNote ?? SAMPLER_PAD_BASE_NOTE;
  const out: SamplerPadRegion[] = [];
  for (let i = 0; i < padCount; i++) {
    const zone = resolvePadZone(preset.zones, i, base);
    if (!zone?.sampleId) {
      continue;
    }
    const start = Math.max(0, Math.floor(zone.region?.start ?? 0));
    const endRaw = zone.region?.end;
    const end =
      endRaw == null
        ? 0
        : Math.max(0, Math.floor(endRaw));
    if (end > 0 && end <= start) {
      continue;
    }
    out.push({
      padIndex: i,
      sampleId: zone.sampleId,
      start,
      end,
      name: `Pad ${String(i + 1).padStart(2, "0")}`,
    });
  }
  return out;
}
